/**
 * Purpose: Normalize business contact info into a typed list so the UI handles
 *          one or many entries per kind (decision D-15). Merges the future
 *          `business.contacts` list with the legacy `contact_phone` field.
 * Inputs: business object.
 * Outputs: getBusinessContacts(business) → [{ kind, value, label, href }].
 *          Kinds: phone | whatsapp | email | website | facebook | instagram |
 *          tiktok | youtube | linkedin | other.
 */
import { splitPhoneNumbers, toWhatsAppUrl } from './phone';

const hrefFor = (kind, value) => {
  switch (kind) {
    case 'phone':
      return `tel:${value}`;
    case 'whatsapp':
      return toWhatsAppUrl(value);
    case 'email':
      return `mailto:${value}`;
    case 'website':
    case 'facebook':
    case 'instagram':
    case 'tiktok':
    case 'youtube':
    case 'linkedin':
      return /^https?:\/\//i.test(value) ? value : `https://${value}`;
    default:
      return null;
  }
};

export const getBusinessContacts = (business) => {
  const explicit = Array.isArray(business?.contacts) ? business.contacts : [];
  const normalized = explicit
    .filter((c) => c && c.value)
    .map((c) => ({
      kind: c.kind || 'other',
      value: c.value,
      label: c.label || null,
      href: hrefFor(c.kind || 'other', c.value),
    }));

  // Legacy contact_phone digits become phone entries unless the explicit list
  // already provides phones (avoids duplicates once Phase 6 data exists).
  if (!normalized.some((c) => c.kind === 'phone')) {
    splitPhoneNumbers(business?.contact_phone).forEach((phone) => {
      normalized.push({ kind: 'phone', value: phone, label: null, href: `tel:${phone}` });
    });
  }

  return normalized;
};

export const contactsOfKind = (contacts, kind) => contacts.filter((c) => c.kind === kind);

/** Best WhatsApp target: explicit whatsapp entry, else first phone. */
export const primaryWhatsAppUrl = (contacts) => {
  const explicit = contacts.find((c) => c.kind === 'whatsapp');
  if (explicit) return explicit.href;
  const phone = contacts.find((c) => c.kind === 'phone');
  return phone ? toWhatsAppUrl(phone.value) : null;
};
